import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import dayjs from 'dayjs'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type IDropdownItem, showToast } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger, truthy } from 'shared/lib'
import { SingletonRequest } from 'shared/api/lib'
import { type IUser } from 'entities/Users'
import { UsersApi } from 'entities/Users/api/users'
import { ContactsApi } from 'entities/Contacts'
import { User } from 'entities/Users/model/User'
import type {
  IResponseBusinessHours,
  IResponseUser,
  IResponseUserTypes,
} from 'entities/Users/api/types'
import { UserOwner } from 'entities/Users/model/UserOwner'
import { ChannelsUser } from 'entities/Users/channels/user'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { userToDropdownItem } from 'entities/Users/lib/userToDropdownItem'
import { eventEmitterStore } from 'entities/EventEmitter'

export class UsersStore {
  initPromise
  initMembersPromise
  private setFinishInit: ((value?: unknown) => void) | null = null
  private setFinishInitMembers: ((value?: unknown) => void) | null = null
  constructor(private _entities: EntitiesStore) {
    makeAutoObservable(this)
    reaction(() => this.user?.awayStatusLastDate, this.handleAwayStatus)
    this.resetPasswordReaction()
    this.initPromise = new Promise((resolve) => {
      this.setFinishInit = resolve
    })
    this.initMembersPromise = new Promise((resolve) => {
      this.setFinishInitMembers = resolve
    })
  }

  loading = true
  error = false
  user_owner_id = 0
  usersMap: Map<number, IUser> = new Map()
  membersIdsMap: Map<number, number> = new Map()
  isRecordAutomatically = false
  isRingEverywhere = false
  awayTimeoutId: NodeJS.Timeout | null = null
  awayModalId: string | null = null
  awayLoading = false

  businessHours: IResponseBusinessHours | null = null
  businessHoursLoading = false

  requestInboxAccessModalId: string | null = null
  requestInboxAccessLoading = false

  private _fetchUsersMe = async () => {
    runInAction(() => {
      this.loading = true
    })

    try {
      const { data: user } = await UsersApi.getUser()

      this._entities.inboxesStore.initInboxCurrent(user)

      runInAction(() => {
        this.user_owner_id = user.id
        this.isRecordAutomatically = user.isRecordAutomatically
        this.isRingEverywhere = user.isRingEverywhere
        new ChannelsUser(this._entities.websocket).subscribeChannels(this.user_owner_id)
        this.addItem(user)

        window.localStorage.setItem('user_id', JSON.stringify(user.id))

        uiStore.setTimezone(user.timezone)
        eventEmitterStore.emit('termly_user_init_logged_in', user.id)
      })

      return user
    } catch (e) {
      logger.error(e)

      runInAction(() => {
        this.error = true
      })

      return null
    } finally {
      runInAction(() => {
        this.loading = false
        this.error = false
      })
    }
  }

  fetchUsersMe = new SingletonRequest(this._fetchUsersMe).request

  init = async () => {
    await this.fetchUsersMe()
    this.setFinishInit?.()
  }

  fetchUsersBusinessHours = async () => {
    try {
      runInAction(() => {
        this.businessHoursLoading = true
      })

      const { data } = await UsersApi.getUsersBusinessHours()

      this.businessHours = data
    } catch (e) {
      console.log(e)
    } finally {
      runInAction(() => {
        this.businessHoursLoading = false
      })
    }
  }

  fetchRequestInboxAccess = async () => {
    const makeRequestInboxAccess = async () => {
      const { data } = await UsersApi.updateRequestInboxAccess()
      if (data.status === 'success' && data.data.id)
        showToast({ type: 'success', title: 'Request sent to your administrator' })
    }

    const onClose = async () => {
      this.requestInboxAccessModalId && modalStore.removeModal(this.requestInboxAccessModalId)

      runInAction(() => {
        this.requestInboxAccessLoading = false
      })
    }

    const showModal = () => {
      this.requestInboxAccessModalId = nanoid()
      modalStore.addModal({
        disabledOnAllClose: true,
        id: this.requestInboxAccessModalId,
        type: ModalTypeList.INFO,
        title: 'Send another request?',
        desc: 'You have already requested access to an inbox. Would you like to send another request to your administrator?',
        onClose,
        primaryAction: {
          text: 'Request inbox access',
          onAction: async () => {
            await makeRequestInboxAccess()
            onClose()
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: onClose,
        },
      })
    }

    try {
      runInAction(() => {
        this.requestInboxAccessLoading = true
      })

      const { data } = await UsersApi.getRequestInboxAccess()

      if (data.status === 'success' && data.data.id) {
        showModal()
      } else {
        makeRequestInboxAccess()
        runInAction(() => {
          this.requestInboxAccessLoading = false
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  get user() {
    return this.getItem(this.user_owner_id) as UserOwner | undefined
  }

  get hasRcsAccess() {
    return Boolean(this.user?.isAdminRole || this.user?.isOwnerRole)
  }

  get accountExistingDays() {
    if (!this.user) return 0

    const createdDate = new Date(this.user?.createdAt).getTime()
    const today = new Date().getTime()

    const diffInMs = today - createdDate

    return Math.floor(diffInMs / (1000 * 60 * 60 * 24))
  }

  get users() {
    return Array.from(this.usersMap.values())
  }

  get membersIdsList() {
    return Array.from(this.membersIdsMap.values())
  }

  get hasMembers() {
    return this.membersIdsList.length !== 0
  }

  get membersCount() {
    return this.membersIdsList.length
  }

  get membersList() {
    return this.membersIdsList
      .reduce<IUser[]>((state, id) => {
        const user = this.getItem(id)

        if (user) state.push(user)

        return state
      }, [])
      .filter((item) => !!item)
  }
  get membersDropdownItems(): IDropdownItem<IUser>[] {
    return this.membersList.map(userToDropdownItem)
  }

  get isAwayStatus() {
    return Boolean(this.user?.isAway)
  }

  addItems = (items: IResponseUser[]) => {
    items = items.filter((item) => item)

    items.forEach((item) => {
      this.addItem(item)
    })
  }

  addMemberIds = (items: IResponseUser[]) => {
    items.forEach((item) => {
      this.addMemberId(item)
    })
  }

  hasItem(id: number) {
    return this.usersMap.has(id)
  }

  addMemberId(item: IResponseUserTypes) {
    this.membersIdsMap.set(item.id, item.id)
  }

  addItem(item: IResponseUserTypes) {
    if (!item) return

    const user = this.usersMap.get(item.id)

    if (item.item_type === 'owner') {
      if (user && user instanceof UserOwner) {
        user.syncOrigin({ ...user.origin, ...item })
      } else {
        this.usersMap.set(item.id, new UserOwner(item, this._entities))
      }
    } else {
      if (item.id === this.user_owner_id) return

      if (user && user instanceof User) {
        user.syncOrigin({ ...user.origin, ...item })
      } else {
        this.usersMap.set(item.id, new User(item))
      }
    }

    return this.usersMap.get(item.id)
  }

  removeItem(id: number) {
    this.usersMap.delete(id)
    this.membersIdsMap.delete(id)
  }

  getItem(id?: number | null, isHard = false) {
    if (!id) return

    const user = this.usersMap.get(id)
    if (user) {
      return user
    }

    if (isHard) {
      UsersApi.getUserById(id)
        .then(({ data }) => {
          if (data) {
            this.addItem(data)
          }
        })
        .catch((e) => logger.error(e))
    }

    return
  }

  getItemById = async (id: number): Promise<IUser | undefined> => {
    const existing = this.usersMap.get(id)
    if (existing) return existing

    try {
      const { data } = await UsersApi.getUserById(id)
      if (data) {
        return this.addItem(data)
      }
    } catch (e) {
      logger.error(e)
    }

    return undefined
  }

  getItemsByIds = async (ids: number[]): Promise<IUser[]> => {
    const users = await Promise.all(
      ids.map(async (id) => {
        const user = this.usersMap.get(id)
        if (user) return user
        const { data } = await UsersApi.getUserById(id)
        return this.addItem(data)
      })
    )
    return users.filter(truthy)
  }

  updateActiveFields = async (fieldName: string) => {
    if (!this.user) return

    const { activeFields } = this.user

    const newActiveFields = activeFields.includes(fieldName)
      ? activeFields.filter((field) => field !== fieldName)
      : [...activeFields, fieldName]

    const { data } = await ContactsApi.updateActiveFields({ contact_fields: newActiveFields })

    runInAction(() => {
      if (this.user?.item_type === 'owner') {
        this.user.saveUpdatedContactFields(data)
      }
    })
  }

  private _initUsersOrganization = async (params?: {
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
  }) => {
    try {
      runInAction(() => {
        this.loading = true
      })

      const users = await UsersApi.getOrganizationMembers(params)

      this.addItems(users.data)
      this.addMemberIds(users.data)

      return users.data
    } catch (e) {
      logger.error(e)

      return []
    } finally {
      runInAction(() => {
        this.loading = false
        this.setFinishInitMembers?.()
      })
    }
  }

  // In-flight dedup: getAppData() and the conversations mount call this concurrently.
  initUsersOrganization = new SingletonRequest(this._initUsersOrganization).request

  handleAwayStatus = () => {
    if (this.awayLoading) return
    if (!this.user) return
    if (this.awayTimeoutId) {
      clearTimeout(this.awayTimeoutId)
    }

    if (this.user.isAway) {
      const showAwayModal = () => {
        if (this.awayModalId) {
          modalStore.removeModal(this.awayModalId)
        }
        this.awayModalId = nanoid()
        if (this.user?.isAway) {
          localStorage.setItem('showedAwayStatusDate', new Date().toISOString())
          const onClose = async () => {
            await this.setAwayStatus(true)
            this.awayModalId && modalStore.removeModal(this.awayModalId)
          }
          modalStore.addModal({
            disabledOnAllClose: true,
            id: this.awayModalId,
            type: ModalTypeList.INFO,
            title: 'Are you still away?',
            desc: 'Switch to appear active',
            onClose,
            primaryAction: {
              text: 'Set to active',
              onAction: async () => {
                await this.setAwayStatus(false)
                this.awayModalId && modalStore.removeModal(this.awayModalId)
              },
            },
            secondaryAction: {
              text: 'Remain away',
              onAction: onClose,
            },
          })
        }
      }

      const showedAwayStatusDate = localStorage.getItem('showedAwayStatusDate')

      const awayTimestamp = dayjs(showedAwayStatusDate).utc().valueOf()
      const currentTimestamp = dayjs().utc().valueOf()
      const fourOurs = 4 * 60 * 60 * 1000
      const diff = currentTimestamp - awayTimestamp

      if (!showedAwayStatusDate || diff > fourOurs) {
        showAwayModal()
      } else {
        this.awayTimeoutId = setTimeout(showAwayModal, fourOurs - diff)
      }
    }
  }

  setAwayStatus = async (status: boolean) => {
    if (this.user?.isAway === undefined) return

    try {
      runInAction(() => {
        this.awayLoading = true
      })

      const res = await UsersApi.toggleAwayStatus({ away_status: status })

      runInAction(() => {
        if (this.user) {
          this.user.isAway = res.data.isAway
          this.user.awayStatusLastDate = res.data.awayStatusLastDate
        }
      })

      return res
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this.awayLoading = false
      })
    }
  }

  toggleAwayStatus = () => {
    this.setAwayStatus(!this.user?.isAway)
  }

  resetPasswordReaction = () => {
    reaction(
      () => this.user?.needPasswordReset,
      (value) => {
        if (value) {
          uiStore.changeRoute({
            path: '/auth/password-change/force',
            type: 'vue',
          })
        }
      }
    )
  }
}
